import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Stack,
  Paper,
  Chip,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  CloudQueue,
  Security,
  Speed,
  Analytics,
  CheckCircle,
  ExpandMore,
  TrendingUp,
  Assessment,
  Storage,
  Api,
  CloudSync,
  Public
} from '@mui/icons-material';

const CloudServices = () => {
  const benefits = [
    'Accelerate your cloud journey with comprehensive cloud services',
    'Optimize cloud performance and reduce operational costs',
    'Enhance security and compliance with cloud-native solutions',
    'Enable faster deployment and time-to-market',
    'Improve scalability and flexibility with cloud infrastructure',
    'Access to advanced cloud services and global reach'
  ];

  const services = [
    {
      title: 'Cloud Strategy and Planning',
      description: 'Develop comprehensive cloud strategies and migration roadmaps',
      features: ['Cloud Assessment', 'Migration Planning', 'Cost Optimization', 'Risk Analysis'],
      benefits: ['Strategic Alignment', 'Cost Reduction', 'Risk Mitigation', 'Faster Implementation']
    },
    {
      title: 'Cloud Migration Services',
      description: 'Seamlessly migrate applications and data to cloud platforms',
      features: ['Application Migration', 'Data Migration', 'Testing and Validation', 'Go-live Support'],
      benefits: ['Reduced Downtime', 'Improved Performance', 'Enhanced Security', 'Cost Savings']
    },
    {
      title: 'Cloud Infrastructure Management',
      description: 'Manage and optimize cloud infrastructure for maximum efficiency',
      features: ['Infrastructure Monitoring', 'Performance Optimization', 'Auto-scaling', 'Disaster Recovery'],
      benefits: ['High Availability', 'Cost Optimization', 'Improved Performance', 'Business Continuity']
    },
    {
      title: 'Multi-Cloud Solutions',
      description: 'Implement and manage multi-cloud environments for maximum flexibility',
      features: ['Multi-Cloud Strategy', 'Cloud Integration', 'Workload Distribution', 'Vendor Management'],
      benefits: ['Vendor Independence', 'Risk Distribution', 'Cost Optimization', 'Flexibility']
    }
  ];

  const customerStories = [
    {
      title: 'Global Manufacturing Company',
      description: 'Transforming operations with cloud-first infrastructure',
      outcome: 'Successfully migrated 500+ applications to cloud, reducing infrastructure costs by 40% while improving performance and scalability across global operations.',
      industry: 'Manufacturing',
      services: ['Cloud Migration', 'Infrastructure Optimization']
    },
    {
      title: 'Financial Services Firm',
      description: 'Enhancing security and compliance with cloud solutions',
      outcome: 'Implemented secure cloud infrastructure with advanced compliance features, enabling faster innovation while meeting strict regulatory requirements.',
      industry: 'Financial Services',
      services: ['Cloud Security', 'Compliance Management']
    }
  ];

  const faqs = [
    {
      question: 'What are cloud services?',
      answer: 'Cloud services are on-demand computing resources and applications delivered over the internet. These services include infrastructure, platforms, and software that can be accessed remotely, providing scalability, flexibility, and cost-effectiveness for businesses.'
    },
    {
      question: 'What are the benefits of cloud migration?',
      answer: 'Cloud migration offers numerous benefits including cost reduction, improved scalability, enhanced security, faster deployment, better disaster recovery, and access to advanced technologies. It also enables remote work capabilities and reduces infrastructure maintenance overhead.'
    },
    {
      question: 'How do you ensure cloud security?',
      answer: 'We implement comprehensive cloud security measures including identity and access management, data encryption, network security, compliance monitoring, and regular security assessments. Our security-first approach ensures your cloud environment meets industry standards and regulatory requirements.'
    }
  ];

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          py: 12,
          textAlign: 'center'
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              fontWeight: 'bold',
              color: '#1a1a1a',
              mb: 3
            }}
          >
            Cloud Services
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: '#666',
              maxWidth: '800px',
              mx: 'auto',
              mb: 4,
              lineHeight: 1.6
            }}
          >
            Accelerate your cloud journey with comprehensive cloud services and expert guidance
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              sx={{
                bgcolor: '#1976d2',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                '&:hover': { bgcolor: '#1565c0' }
              }}
            >
              Get Started
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{
                borderColor: '#1976d2',
                color: '#1976d2',
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                '&:hover': {
                  borderColor: '#1565c0',
                  color: '#1565c0',
                  bgcolor: 'rgba(25, 118, 210, 0.04)'
                }
              }}
            >
              Learn More
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* What We Do Section */}
      <Box sx={{ py: 10, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 'bold',
              color: '#1a1a1a',
              mb: 8
            }}
          >
            Transform Your Business with Cloud Technology
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: '#666',
              maxWidth: '800px',
              mx: 'auto',
              mb: 8,
              lineHeight: 1.6
            }}
          >
            Cloud migration is the process of moving digital assets, applications, and IT resources to the cloud. 
            This transformation helps enterprises improve performance, efficiency, and scalability while reducing 
            infrastructure costs and maintenance overhead.
          </Typography>
        </Container>
      </Box>

      {/* Services Section */}
      <Box sx={{ py: 10, bgcolor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 'bold',
              color: '#1a1a1a',
              mb: 8
            }}
          >
            Our Cloud Services
          </Typography>

          <Grid container spacing={4}>
            {services.map((service, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 'bold',
                        color: '#1a1a1a',
                        mb: 2
                      }}
                    >
                      {service.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#666',
                        mb: 3,
                        lineHeight: 1.6
                      }}
                    >
                      {service.description}
                    </Typography>
                    
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, color: '#1976d2' }}>
                        Key Features:
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {service.features.map((feature, idx) => (
                          <Chip
                            key={idx}
                            label={feature}
                            size="small"
                            sx={{
                              bgcolor: '#e3f2fd',
                              color: '#1976d2',
                              fontWeight: 'bold'
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>

                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, color: '#1976d2' }}>
                        Benefits:
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {service.benefits.map((benefit, idx) => (
                          <Chip
                            key={idx}
                            label={benefit}
                            size="small"
                            sx={{
                              bgcolor: '#f3e5f5',
                              color: '#7b1fa2',
                              fontWeight: 'bold'
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Customer Stories Section */}
      <Box sx={{ py: 10, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 'bold',
              color: '#1a1a1a',
              mb: 8
            }}
          >
            Customer Success Stories
          </Typography>

          <Grid container spacing={4}>
            {customerStories.map((story, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 'bold',
                        color: '#1a1a1a',
                        mb: 2
                      }}
                    >
                      {story.title}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        color: '#1976d2',
                        mb: 2
                      }}
                    >
                      {story.description}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#666',
                        mb: 3,
                        lineHeight: 1.6
                      }}
                    >
                      {story.outcome}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Chip
                        label={story.industry}
                        sx={{
                          bgcolor: '#e8f5e8',
                          color: '#2e7d32',
                          fontWeight: 'bold'
                        }}
                      />
                      <Stack direction="row" spacing={1}>
                        {story.services.map((service, idx) => (
                          <Chip
                            key={idx}
                            label={service}
                            size="small"
                            sx={{
                              bgcolor: '#e3f2fd',
                              color: '#1976d2',
                              fontWeight: 'bold'
                            }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ py: 10, bgcolor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 'bold',
              color: '#1a1a1a',
              mb: 8
            }}
          >
            Frequently Asked Questions
          </Typography>

          <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
            {faqs.map((faq, index) => (
              <Accordion
                key={index}
                elevation={0}
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  mb: 2,
                  '&:before': { display: 'none' }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{
                    bgcolor: 'white',
                    borderRadius: 2,
                    '&:hover': { bgcolor: '#f5f5f5' }
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a1a1a' }}>
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ bgcolor: 'white', borderRadius: '0 0 8px 8px' }}>
                  <Typography variant="body1" sx={{ color: '#666', lineHeight: 1.6 }}>
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: 10, bgcolor: '#1976d2', color: 'white' }}>
        <Container maxWidth="lg">
          <Paper
            elevation={0}
            sx={{
              p: 6,
              textAlign: 'center',
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              borderRadius: 2,
              backdropFilter: 'blur(10px)'
            }}
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: 'bold',
                mb: 3
              }}
            >
              Start Your Cloud Journey
            </Typography>
            <Typography
              variant="h5"
              sx={{
                mb: 4,
                opacity: 0.9
              }}
            >
              Get a 30-minute, no-cost strategy session with a Cloud expert
            </Typography>
            <Button
              variant="contained"
              size="large"
              sx={{
                bgcolor: 'white',
                color: '#1976d2',
                px: 6,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                '&:hover': {
                  bgcolor: '#f5f5f5'
                }
              }}
            >
              Request a Consultation
            </Button>
          </Paper>
        </Container>
      </Box>
    </>
  );
};

export default CloudServices;
